import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Printer } from "lucide-react";
import { RECEIPT_PRINT_EVENT } from "@/features/payments/services/paymentReceiptPdf";

export const ReceiptPrintModal = () => {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [printed, setPrinted] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      setBlob((event as CustomEvent<Blob>).detail);
      setPrinted(false);
      setPreviewLoaded(false);
    };
    window.addEventListener(RECEIPT_PRINT_EVENT, handler);
    return () => window.removeEventListener(RECEIPT_PRINT_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!blob) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  const close = () => setBlob(null);

  const handlePrint = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setPrinted(true);
  };

  return (
    <Dialog open={!!blob} onOpenChange={(open) => !open && close()}>
      <DialogContent className="flex h-[90vh] max-w-2xl flex-col gap-0 p-0">
        <DialogHeader className="shrink-0 border-b px-4 py-3">
          <DialogTitle>Receipt Preview</DialogTitle>
        </DialogHeader>

        <div className="relative flex-1 overflow-hidden">
          {blobUrl && (
            <iframe
              ref={iframeRef}
              src={`${blobUrl}#toolbar=0`}
              title="Receipt"
              className="h-full w-full border-0"
              onLoad={() => setPreviewLoaded(true)}
            />
          )}
          {blobUrl && !previewLoaded && (
            <div
              role="status"
              className="absolute inset-0 flex items-center justify-center gap-2 bg-background text-sm text-muted-foreground"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading receipt preview…
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t px-4 py-3">
          <Button variant="outline" onClick={close}>
            Close
          </Button>
          <Button
            onClick={handlePrint}
            disabled={printed}
            className="bg-green-700 hover:bg-green-800 text-white"
          >
            <Printer className="mr-2 h-4 w-4" />
            Print Receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
